import { isEqual } from 'lodash';
import templates from 'framework/router/page-templates';
import configs from 'framework/router/route-data-configs';

/**
 * Compare two route definitions, ignoring irrelevant information such as hash
 * @access public
 * @param {object} r1 - A route record describing the previous location
 * @param {object} r2 - A route record describing the next location
 * @return {object} An object with values comparing certain aspects of the two records
 */
function compareRecords(r1, r2) {
    const hash = r1.hash === r2.hash;
    const name = r1.name === r2.name;
    const params = isEqual(r1.params, r2.params);
    const query = isEqual(r1.query, r2.query);

    return { hash, name, params, query };
}

/**
 * Check two route definitions whether there are only hash changes
 * @param {object} r1 - A route record describing the previous location
 * @param {object} r2 - A route record describing the next location
 * @return {boolean} The result of the comparison analysis
 */
function isHashChange(r1, r2) {
    const { hash, name, params, query } = compareRecords(r1, r2);
    return !hash && name && params && query;
}

/**
 * Funtion called before vue router transition
 * @param {object} Log - Router logger
 * @param {string} initialRoute - The initial url
 * @return {function} The before each hook
 */
export function createBeforeEachHook(Log, initialRoute) {
    return function beforeEachHook(to, from, next) {
        const hasMatches = (Array.isArray(to.matched) && to.matched.length);
        const isInitialRoute = (initialRoute === to.fullPath);

        // Simple link without corresponding route record
        if (!hasMatches && !isInitialRoute && !isHashChange(to, from)) {
            Log.debug('No matches found, following path url');
            window.location.href = to.path;
            return;
        }

        // Registered route record
        Log.debug('Following matched route');
        next();
    };
}

/**
 * Route config creation helper
 * @param {Array} records - A route record array describing a list of available routes
 * @param {Array} middlewares - A list of middlewares to apply to each route (also recursively)
 * @param {Array} parents - The records' parental hierarchy, the first item being the oldest
 * @return {Array} The enhanced route records, ready for being used by `vue-router`
 */
export function decorateRecords(records = [], middlewares = [], parents = []) {
    return middlewares.reduce((acc, middleware) => acc.map(record => middleware(record, parents, middlewares)), records);
}

/**
 * Fetch all records of a route config
 * @param {string} config - The config name
 * @param {any[]} routes - A list of routes
 * @return {any[]} A list of routes
 */
export function getRecords({ config, routes }) {
    if (Array.isArray(routes)) {
        return routes;
    }

    return configs[config].routes;
}

/**
 * Preprocess a route record coming from the server as these must be JSON compliant data
 * @param {IRouteRecord} record - The route record coming from the server
 * @return {IRouteRecord} The transformed record
 */
export function prepareRecord(record) {
    const { redirect, alias, component } = record;

    if (!redirect && !alias) {
        record.component = templates[component];
    }

    return record;
}
